/*
 * <delivery-estimate> — product-page "Get it by [date]" widget.
 * POSTs the product's weight + L/W/H to the Cloudflare delivery Worker and renders
 * the returned UPS Ground arrival date. On failure: hide, or show a static
 * business-day range (admin toggle). ZIP is remembered in localStorage.
 *
 * Worker contract (see stl-delivery-worker): POST {weight,length,width,height,qty,zip?,prod_id?}
 *   -> { success, days, location:{city,state,zip,...}, delivery_date:{compact_date,...} }
 *   -> { success:false, reason, message }
 */
(function () {
  const STORAGE_KEY = "stl_delivery_zip";

  function num(v) {
    const n = parseFloat(v);
    return isFinite(n) && n > 0 ? n : null;
  }

  // Shopify Liquid `variant.weight` is ALWAYS grams (its `weight_unit` is only a display
  // preference), so convert grams -> lbs for OSCO, which we send as UOM=LBS.
  const GRAMS_PER_LB = 453.59237;
  function gramsToLbs(grams) {
    const g = parseFloat(grams);
    if (!isFinite(g) || g <= 0) return null;
    return Math.round((g / GRAMS_PER_LB) * 100) / 100;
  }

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this._init) return; // guard against re-upgrade in the theme editor
      this._init = true;

      this.workerUrl = (this.dataset.workerUrl || "").trim();
      this.prodId = this.dataset.prodId || "";
      this.allowZip = this.dataset.allowZip !== "false";
      this.locale = this.dataset.locale || "en-US";
      this.heading = this.dataset.heading || "Get it by";
      this.fallbackEnabled = this.dataset.fallbackEnabled === "true";

      this.dims = {
        weight: gramsToLbs(this.dataset.weightGrams),
        length: num(this.dataset.length),
        width: num(this.dataset.width),
        height: num(this.dataset.height),
      };

      this.msgEl = this.querySelector("[data-de-message]");
      this.locRow = this.querySelector("[data-de-location-row]");
      this.locEl = this.querySelector("[data-de-location]");
      this.editor = this.querySelector("[data-de-editor]");
      this.zipInput = this.querySelector("[data-de-zip-input]");
      this.zipError = this.querySelector("[data-de-zip-error]");

      this.bindEvents();

      this.haveProductDims = !!(this.dims.length && this.dims.width && this.dims.height);

      // No Worker URL, or the product carries no dimensions at all -> a live estimate is
      // impossible (dims are product-level, no variant can supply them). Hide or fall back,
      // and don't bother subscribing to variant changes.
      if (!this.workerUrl || !this.haveProductDims) {
        this.degrade();
        return;
      }

      // Weight is per-variant: refresh the estimate when the shopper switches variant.
      this.subscribeVariantChange();

      // Initial estimate runs silently (element stays hidden until we have a result,
      // so a failure->hide never flashes).
      this.fetchEstimate(this.getSavedZip(), { silent: true });
    }

    bindEvents() {
      const change = this.querySelector("[data-de-change]");
      if (change) change.addEventListener("click", (e) => { e.preventDefault(); this.openEditor(); });
      const cancel = this.querySelector("[data-de-zip-cancel]");
      if (cancel) cancel.addEventListener("click", (e) => { e.preventDefault(); this.closeEditor(); });
      const update = this.querySelector("[data-de-zip-update]");
      if (update) update.addEventListener("click", () => this.submitZip());
      if (this.zipInput) {
        this.zipInput.addEventListener("keydown", (e) => {
          if (e.key === "Enter") { e.preventDefault(); this.submitZip(); }
        });
      }
    }

    getSavedZip() {
      try { return localStorage.getItem(STORAGE_KEY) || null; } catch (_) { return null; }
    }
    saveZip(zip) {
      try { localStorage.setItem(STORAGE_KEY, zip); } catch (_) {}
    }

    async fetchEstimate(zip, opts) {
      const silent = opts && opts.silent;
      if (!this.dims.weight) { this.degrade(); return; } // this variant has no usable weight
      if (!silent) { this.setMessage("Checking delivery date…"); this.show(); }

      const body = {
        weight: this.dims.weight,
        length: this.dims.length,
        width: this.dims.width,
        height: this.dims.height,
        qty: 1,
      };
      if (this.prodId) body.prod_id = this.prodId;
      if (zip) body.zip = zip;

      try {
        const res = await fetch(this.workerUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data && data.success) {
          this.renderSuccess(data);
        } else if (zip && data && (data.reason === "invalid_zip" || data.reason === "zip_lookup_failed")) {
          // A ZIP the shopper typed didn't resolve — keep the editor open with an inline error.
          this.showZipError(data.message || "Could not look up that ZIP code");
        } else {
          this.degrade();
        }
      } catch (_) {
        this.degrade();
      }
    }

    renderSuccess(data) {
      const dd = data.delivery_date || {};
      const dateText = dd.compact_date || dd.delivery_date || data.days || "";
      this.setMessage((this.heading + " " + dateText).trim());

      if (this.allowZip && this.locRow && this.locEl && data.location) {
        const loc = data.location;
        const label = [loc.city, loc.state].filter(Boolean).join(", ") || loc.zip || "";
        if (label) {
          this.locEl.textContent = "Shipping to " + label;
          this.locRow.hidden = false;
        }
      }
      this.closeEditor();
      this.show();
      this.flash();
    }

    degrade() {
      if (this.fallbackEnabled) this.renderFallback();
      else this.hide();
    }

    renderFallback() {
      const from = parseInt(this.dataset.fallbackFrom, 10);
      const to = parseInt(this.dataset.fallbackTo, 10);
      if (!isFinite(from) || !isFinite(to)) { this.hide(); return; }

      const heading = this.dataset.fallbackHeading || "Order now to receive by";
      const skipSat = this.dataset.skipSaturday !== "false";
      const skipSun = this.dataset.skipSunday !== "false";
      const opts = { day: "numeric", month: "short", weekday: "short" };
      const fromStr = addBusinessDays(new Date(), from, skipSat, skipSun).toLocaleDateString(this.locale, opts);
      const toStr = addBusinessDays(new Date(), to, skipSat, skipSun).toLocaleDateString(this.locale, opts);

      this.setMessage(heading + " " + fromStr + " - " + toStr);
      if (this.locRow) this.locRow.hidden = true; // no live location in fallback mode
      this.show();
    }

    openEditor() {
      if (!this.editor) return;
      this.editor.hidden = false;
      if (this.zipInput) { this.zipInput.value = this.getSavedZip() || ""; this.zipInput.focus(); }
    }
    closeEditor() { if (this.editor) this.editor.hidden = true; this.hideZipError(); }
    showZipError(msg) {
      if (this.zipError) { this.zipError.textContent = msg; this.zipError.hidden = false; }
      this.openEditor();
    }
    hideZipError() { if (this.zipError) this.zipError.hidden = true; }

    submitZip() {
      const zip = ((this.zipInput && this.zipInput.value) || "").trim();
      if (!/^\d{5}$/.test(zip)) { this.showZipError("Please enter a valid 5-digit US ZIP code"); return; }
      this.saveZip(zip);
      this.hideZipError();
      this.fetchEstimate(zip);
    }

    setMessage(text) { if (this.msgEl) this.msgEl.textContent = text; }
    show() { this.hidden = false; }
    hide() { this.hidden = true; }
    flash() {
      this.classList.add("de-flash");
      setTimeout(() => this.classList.remove("de-flash"), 600);
    }

    subscribeVariantChange() {
      if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined" || !PUB_SUB_EVENTS.variantChange) return;
      this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => this.onVariantChange(payload));
    }

    onVariantChange(payload) {
      const data = payload && payload.data;
      if (!data || data.variant === null) return; // ignore unavailable option combinations

      // product-info.js re-renders the section for the new variant but does NOT replace this
      // element, so read the new (unit-consistent) weight from the rendered <delivery-estimate>
      // inside the event's html. Dims are product-level and unchanged.
      const fresh = data.html && typeof data.html.querySelector === "function"
        ? data.html.querySelector("delivery-estimate")
        : null;
      if (fresh) {
        const w = gramsToLbs(fresh.dataset.weightGrams);
        if (w) this.dims.weight = w;
      }

      // Debounce rapid option clicks into a single refetch.
      clearTimeout(this._variantTimer);
      this._variantTimer = setTimeout(() => this.fetchEstimate(this.getSavedZip()), 250);
    }

    disconnectedCallback() {
      if (this._unsubscribe) this._unsubscribe();
      clearTimeout(this._variantTimer);
    }
  }

  function addBusinessDays(date, days, skipSaturday, skipSunday) {
    const result = new Date(date.getTime());
    let count = 0;
    while (count < days) {
      result.setDate(result.getDate() + 1);
      const d = result.getDay();
      if ((d === 6 && skipSaturday) || (d === 0 && skipSunday)) continue;
      count++;
    }
    return result;
  }

  if (!customElements.get("delivery-estimate")) {
    customElements.define("delivery-estimate", DeliveryEstimate);
  }
})();
